import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Row, Col, Input, message, Popconfirm, Switch } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined,
  UserOutlined,
  SaveOutlined,
  CloseOutlined,
  SendOutlined
} from '@ant-design/icons';
import axios from 'axios';

const MobileTelegramUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ ime: '', chat_id: '', aktivan: true });

  // Stanja za poruke
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isDirectMode, setIsDirectMode] = useState(false);
  const [directTarget, setDirectTarget] = useState(null);
  const [directMessage, setDirectMessage] = useState('');
  const [sending, setSending] = useState(false);

  const USERS_URL = '/api/box/telegram-users/';

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(USERS_URL);
      setUsers(res.data);
    } catch (error) {
      message.error('Error loading Telegram users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openOverlay = (user = null) => {
    setIsDirectMode(false); // Resetuj mod na edit/add
    if (user) {
      setEditingUser(user);
      setFormData({ ime: user.ime, chat_id: user.chat_id, aktivan: user.aktivan });
    } else {
      setEditingUser(null);
      setFormData({ ime: '', chat_id: '', aktivan: true });
    }
    setIsOverlayOpen(true);
  };

  const openDirectModal = (record) => {
    setIsDirectMode(true);
    setDirectTarget(record);
    setDirectMessage('');
    setIsOverlayOpen(true);
  };

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) return message.warning('Enter message');
    setSending(true);
    try {
      await axios.post(`${USERS_URL}send_message/`, { message: broadcastMessage });
      message.success('Broadcast sent!');
      setBroadcastMessage('');
    } catch (error) {
      message.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleDirectSend = async () => {
    if (!directMessage.trim()) return message.warning('Enter message');
    setSending(true);
    try {
      await axios.post(`${USERS_URL}${directTarget.id}/send_direct_message/`, { message: directMessage });
      message.success(`Sent to ${directTarget.ime}`);
      setIsOverlayOpen(false);
    } catch (error) {
      message.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSave = async () => {
    if (!formData.ime || !formData.chat_id) {
      message.warning('Name and Chat ID are required');
      return;
    }
    try {
      if (editingUser) {
        await axios.put(`${USERS_URL}${editingUser.id}/`, formData);
        message.success('User updated');
      } else {
        await axios.post(USERS_URL, formData);
        message.success('User added');
      }
      setIsOverlayOpen(false);
      fetchData();
    } catch (error) {
      message.error('Error saving user');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${USERS_URL}${id}/`);
      message.success('User removed');
      fetchData();
    } catch (error) {
      message.error('Error deleting user');
    }
  };

  const columns = [
    {
      title: 'Users',
      key: 'user_card',
      render: (record) => (
        <div className="m-user-card">
          <div className="m-user-info">
            <div className="m-user-main">
              <UserOutlined style={{ color: record.aktivan ? '#3b82f6' : '#64748b' }} />
              <span className="m-user-name">{record.ime}</span>
            </div>
            <div className="m-user-chatid">ID: {record.chat_id}</div>
          </div>
          
          <div className="m-user-status">
            <span style={{ color: '#64748b', fontSize: '11px' }}>ACTIVE:</span>
            <Switch checked={record.aktivan} size="small" disabled style={{ marginLeft: '8px' }} />
          </div>

          <div className="m-user-actions">
            <Button size="small" icon={<SendOutlined />} onClick={() => openDirectModal(record)} style={{ background: '#065f46', color: '#34d399', border: '1px solid #064e3b' }}>Msg</Button>
            <Button size="small" icon={<EditOutlined />} onClick={() => openOverlay(record)} style={{ background: '#1e293b', color: '#60a5fa', border: '1px solid #334155' }}>Edit</Button>
            <Popconfirm title="Delete user?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
              <Button size="small" icon={<DeleteOutlined />} style={{ background: '#1e293b', color: '#ef4444', border: '1px solid #334155' }} />
            </Popconfirm>
          </div>
        </div>
      )
    }
  ];

  return (
    <div>
      {/* BROADCAST SEKCIJA */}
      <div className="m-broadcast-box" style={{ marginBottom: '15px' }}>
         <div className="m-label" style={{ color: '#10b981', marginBottom: '8px' }}>SEND ADMIN MESSAGE (ALL)</div>
         <div style={{ display: 'flex', gap: '8px' }}>
            <Input 
              placeholder="Message to all..." 
              value={broadcastMessage}
              onChange={e => setBroadcastMessage(e.target.value)}
              style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
            />
            <Button type="primary" icon={<SendOutlined />} loading={sending} onClick={handleBroadcast} style={{ background: '#10b981', border: 'none' }} />
         </div>
      </div>

      <Row gutter={[8, 8]} style={{ marginBottom: '15px' }}>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6' }}>
            <Space>
              <UserOutlined style={{ color: '#3b82f6' }} />
              <Space>
                <span style={{ color: '#64748b', fontSize: '11px' }}>TOTAL:</span>
                <span style={{ color: 'white', fontWeight: 'bold' }}>{users.length}</span>
              </Space>
            </Space>
          </div>
        </Col>
        <Col span={12}>
          <div className="st-box" style={{ borderBottomColor: '#3b82f6', cursor: 'pointer', padding: '0' }} onClick={() => openOverlay()}>
            <Button block type="dashed" icon={<PlusOutlined />} style={{ background: 'transparent', color: '#3b82f6', border: 'none', height: '45px', fontWeight: 'bold' }}>
              ADD USER
            </Button>
          </div>
        </Col>
      </Row>

      <Table dataSource={users} columns={columns} rowKey="id" loading={loading} pagination={false} className="m-user-table" />

      {isOverlayOpen && (
        <div className="m-custom-overlay">
          <div className="m-overlay-content">
            <div className="m-overlay-header">
              <span>
                {isDirectMode ? `SEND TO ${directTarget?.ime}` : (editingUser ? 'EDIT TELEGRAM USER' : 'ADD TELEGRAM USER')}
              </span>
              <Button type="text" icon={<CloseOutlined style={{ color: 'white' }} />} onClick={() => setIsOverlayOpen(false)} />
            </div>
            
            <div className="m-overlay-body">
              {isDirectMode ? (
                /* MOD ZA DIREKTNU PORUKU */
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="m-label">MESSAGE</label>
                    <Input.TextArea 
                      rows={4}
                      value={directMessage}
                      onChange={e => setDirectMessage(e.target.value)}
                      placeholder="Type message..."
                      style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                    />
                  </div>
                  <Button block type="primary" icon={<SendOutlined />} onClick={handleDirectSend} loading={sending} style={{ background: '#10b981', height: '45px', fontWeight: 'bold', border: 'none' }}>
                    SEND MESSAGE
                  </Button>
                </>
              ) : (
                /* MOD ZA EDIT/ADD KORISNIKA */
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="m-label">NAME</label>
                    <Input 
                      value={formData.ime} 
                      onChange={e => setFormData({...formData, ime: e.target.value})}
                      style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label className="m-label">CHAT ID</label>
                    <Input 
                      value={formData.chat_id} 
                      onChange={e => setFormData({...formData, chat_id: e.target.value})}
                      style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }}
                    />
                  </div>
                  <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label className="m-label" style={{ marginBottom: 0 }}>ACTIVE STATUS:</label>
                    <Switch checked={formData.aktivan} onChange={v => setFormData({...formData, aktivan: v})} />
                  </div>
                  <Button block type="primary" icon={<SaveOutlined />} onClick={handleSave} style={{ background: '#3b82f6', height: '45px', fontWeight: 'bold', border: 'none' }}>
                    SAVE USER
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .st-box { background: #1e293b; padding: 12px; border-radius: 8px; border: 1px solid #334155; border-bottom: 3px solid; display: flex; justify-content: center; align-items: center; }
        .m-broadcast-box { background: #1e293b; padding: 12px; border-radius: 8px; border: 1px solid #334155; border-bottom: 3px solid #10b981; }
        
        .m-user-table .ant-table { background: transparent !important; }
        .m-user-table .ant-table-thead { display: none !important; }
        .m-user-table .ant-table-tbody > tr > td { border: none !important; padding: 0 0 12px 0 !important; }
        .m-user-table .ant-table-tbody > tr:hover > td { background: transparent !important; }

        .m-user-card { background: #1e293b; border-radius: 8px; border: 1px solid #334155; padding: 12px; }
        .m-user-info { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .m-user-main { display: flex; align-items: center; gap: 10px; }
        .m-user-name { color: white; font-weight: bold; font-size: 14px; }
        .m-user-chatid { color: #3b82f6; font-size: 12px; font-weight: bold; }
        .m-user-status { margin-bottom: 12px; display: flex; align-items: center; }
        .m-user-actions { display: flex; gap: 8px; justify-content: flex-end; }

        .m-custom-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.85); z-index: 9999; display: flex; align-items: center; justify-content: center; }
        .m-overlay-content { background: #1e293b; width: 90%; max-width: 400px; border-radius: 12px; border: 1px solid #334155; overflow: hidden; box-shadow: 0 0 20px rgba(0,0,0,0.5); }
        .m-overlay-header { padding: 15px; border-bottom: 1px solid #334155; display: flex; justify-content: space-between; align-items: center; color: white; font-weight: bold; font-size: 13px; text-transform: uppercase; }
        .m-overlay-body { padding: 20px; }
        .m-label { color: #64748b; font-size: 10px; font-weight: bold; display: block; margin-bottom: 5px; text-transform: uppercase; }
      `}</style>
    </div>
  );
};

export default MobileTelegramUsers;